import usePrivateAxios from "@/common/api/usePrivateAxios";
import { ErrorResponse } from "@/common/types/errorResponse";
import { getRailsUpdate } from "@/common/utils/getRailsUpdate";
import { intervalToSeconds } from "@/common/utils/intervalToSeconds";
import { mapToDeletion } from "@/common/utils/railsHelpers";
import { unFormatCurrency, unFormatUnit } from "@/common/utils/unFormatters";
import { CompanyBranchValidationSchema } from "@/features/privateCompanies/schemas/createBranch.schema";
import { CompanyBranchData } from "@/features/privateCompanies/types/companyBranches.type";
import { transformNewProductCategoryRestrictions, transformUpdatedProductCategoryRestrictions } from "@/features/privateCompanies/utils/productCategoryRestrictionsTransformers";
import { useMutation } from "@tanstack/react-query";
import { transformNewProductCategoryGrantedCredit } from "../services/companyBranchTransformer";
import { ControlledByType } from "../types/companyGovernmentAllocation.type";

interface UseUpdateCompanyBranch {
  branchId: string | undefined,
  branchData: CompanyBranchData | undefined,
  controlledBy?: ControlledByType,
  onSuccess: () => void,
  onError: (error: ErrorResponse) => void,
}

const useUpdateCompanyBranch = ({
  branchId,
  branchData,
  controlledBy,
  onSuccess,
  onError,
}: UseUpdateCompanyBranch) => {
  const privateAxios = usePrivateAxios();
  return useMutation({
    mutationKey: ['updateCompanyBranch'],
    mutationFn: async ({ form, file }: { form: CompanyBranchValidationSchema, file?: File }) => {
      const minimum_kilometers_usage_between_consumptions = form.minimum_kilometers_usage_between_consumptions
        ? (unFormatUnit(form.minimum_kilometers_usage_between_consumptions, 'Km') || '')
        : '';
      const maximum_kilometers_usage_between_consumptions = form.maximum_kilometers_usage_between_consumptions
        ? (unFormatUnit(form.maximum_kilometers_usage_between_consumptions, 'Km') || '')
        : '';
      const minimum_seconds_usage_between_consumptions = intervalToSeconds({
        days: Number(form.minimum_seconds_usage_between_consumptions?.days || 0),
        hours: Number(form.minimum_seconds_usage_between_consumptions?.hours || 0),
        minutes: Number(form.minimum_seconds_usage_between_consumptions?.minutes || 0),
        seconds: Number(form.minimum_seconds_usage_between_consumptions?.seconds || 0),
      });
      const maximum_seconds_usage_between_consumptions = intervalToSeconds({
        days: Number(form.maximum_seconds_usage_between_consumptions?.days || 0),
        hours: Number(form.maximum_seconds_usage_between_consumptions?.hours || 0),
        minutes: Number(form.maximum_seconds_usage_between_consumptions?.minutes || 0),
        seconds: Number(form.maximum_seconds_usage_between_consumptions?.seconds || 0),
      });
      const minimum_seconds_interval_between_consumptions = intervalToSeconds({
        days: Number(form.minimum_seconds_interval_between_consumptions?.days || 0),
        hours: Number(form.minimum_seconds_interval_between_consumptions?.hours || 0),
        minutes: Number(form.minimum_seconds_interval_between_consumptions?.minutes || 0),
        seconds: Number(form.minimum_seconds_interval_between_consumptions?.seconds || 0),
      });

      const payload = {
        ...form,
        parent_id: form.parent?.value,
        should_have_own_bill_when_dynamic: form.should_have_own_bill_when_dynamic,
        master_client_user_id: form.master_client_user_id?.value,
        general_information_attributes: form.general_information?.willUseOwnData
          ? {
            ...form.general_information,
            address_attributes: {
              ...form.general_information?.address_attributes,
              city_id: form.general_information?.address_attributes?.city?.id,
            },
            company_logo: file || undefined,
          } : branchData?.general_information ? {
            id: branchData?.general_information.id,
            _destroy: true
          } : undefined,
        restriction_attributes: {
          establishment_block_mode:
            form.enableEstablishmentRestriction
              ? form.establishment_block_mode?.value
              : undefined,
          allowed_or_blocked_establishments_attributes:
            form.enableEstablishmentRestriction
              ? getRailsUpdate(
                branchData?.restriction.allowed_or_blocked_establishments || [],
                form?.allowed_or_blocked_establishments_attributes || [],
                (establishment) => establishment.establishment_branch.id,
                (formEstablishment) => formEstablishment.value,
                mapToDeletion,
                (newEstablishment) => ({ establishment_branch_id: newEstablishment.value }),
              )
              : branchData?.restriction.allowed_or_blocked_establishments?.map(mapToDeletion) || undefined,
          period_restrictions_attributes: form.enablePeriodRestriction
            ? form.period_restrictions_attributes?.map((periodRestriction, index) => {
              return {
                id: periodRestriction.id,
                week_day: periodRestriction.week_day,
                intervals_attributes: [
                  ...(() => {
                    const deletedIntervals = [] as { id: string, _destroy: boolean; }[];
                    branchData?.restriction.period_restrictions?.[index]?.intervals?.forEach((interval) => {
                      deletedIntervals.push({
                        id: interval.id,
                        _destroy: true
                      });
                    });
                    return deletedIntervals;
                  })(),
                  ...(() => {
                    const newIntervals = [] as { start_time?: string | null, end_time?: string | null; }[];

                    if (periodRestriction.blocked) {
                      return [];
                    }

                    if (periodRestriction.intervals_attributes?.some((interval) => interval.start_time?.value === 'all_day')) {
                      newIntervals.push({ start_time: '00:00', end_time: '23:59' });
                      return newIntervals;
                    }

                    periodRestriction?.intervals_attributes?.forEach((interval) => {
                      newIntervals.push({ start_time: interval.start_time?.value, end_time: interval.end_time?.value });
                    });
                    return newIntervals;
                  })(),
                ]
              };
            })
            : branchData?.restriction?.period_restrictions?.length ? [
              ...branchData?.restriction?.period_restrictions.map((periodRestriction) => ({
                id: periodRestriction.id,
                _destroy: true
              }))
            ] : undefined,
          minimum_kilometers_usage_between_consumptions,
          maximum_kilometers_usage_between_consumptions,
          minimum_seconds_usage_between_consumptions:
            minimum_seconds_usage_between_consumptions > 0
              ? minimum_seconds_usage_between_consumptions
              : '',
          maximum_seconds_usage_between_consumptions:
            maximum_seconds_usage_between_consumptions > 0
              ? maximum_seconds_usage_between_consumptions
              : '',
          minimum_seconds_interval_between_consumptions:
            minimum_seconds_interval_between_consumptions > 0
              ? minimum_seconds_interval_between_consumptions
              : '',
          product_category_restrictions_attributes: [
            ...getRailsUpdate(
              branchData?.restriction.product_category_restrictions || [],
              form?.product_category_restrictions_attributes || [],
              (categoryRestriction) => categoryRestriction.product_category.id,
              (formCategoryRestriction) => formCategoryRestriction.product_category_id,
              mapToDeletion,
              transformNewProductCategoryRestrictions,
            ),
            ...(form.product_category_restrictions_attributes
              ?.filter((categoryRestriction) => !!categoryRestriction.id)
              ?.map(transformUpdatedProductCategoryRestrictions) || []),
          ],
        },
        granted_credit: form.granted_credit ? unFormatCurrency(form.granted_credit) : undefined,
        product_category_granted_credits_attributes: form.enableCreditConfiguration ? [
          ...branchData?.product_category_granted_credits?.map(mapToDeletion) || [],
          ...form?.product_category_granted_credits_attributes?.map(
            (category) => transformNewProductCategoryGrantedCredit(category, controlledBy)
          ) || [],
        ] : branchData?.product_category_granted_credits?.map(mapToDeletion) || undefined,
        fiscal_document_information_attributes: {
          fiscal_document_kind: form.fiscal_document_information_attributes?.fiscal_document_kind?.value || '',
          consumption_observations: form.fiscal_document_information_attributes?.consumption_observations || '',
          service_observations: form.fiscal_document_information_attributes?.service_observations || '',
        },
        name_on_card: form.name_on_card ? form.name_on_card.trim() : '',
        additional_documents_attachments_attributes: getRailsUpdate(
          branchData?.additional_documents || [],
          form?.additional_documents_attachments_attributes || [],
          (attachment) => attachment.id,
          (attachment) => attachment.id,
          mapToDeletion,
          (attachment) => ({ blob_id: attachment.id }),
        )
      };

      delete payload.establishment_block_mode

      return privateAxios.putForm(`/client/company_branches/${branchId}`, { company_branch: payload });
    },
    onSuccess,
    onError,
  });
};

export default useUpdateCompanyBranch;
